import { Info } from 'lucide-react';
import type { InventoryFormValues } from '../../types/inventoryForm.types';

interface TechnicalSpecSectionProps {
    values: InventoryFormValues;
    onChange: <K extends keyof InventoryFormValues>(key: K, value: InventoryFormValues[K]) => void;
}

function FieldLabel({ text, required = false }: { text: string; required?: boolean }) {
    return (
        <label className="mb-2 block sm:text-sm text-xs text-slate-700">
            {text}
            {required ? <span className="text-red-500"> *</span> : null}
        </label>
    );
}

export function TechnicalSpecSection({ values, onChange }: TechnicalSpecSectionProps) {
    return (
        <div className="rounded-md border border-slate-200 bg-white p-3 space-y-4">
            <h3 className="sm:text-sm text-xs text-slate-900 border-b border-slate-100 pb-2 flex items-center gap-2">
                <Info className="h-4 w-4 text-emerald-600" />
                Thông số kỹ thuật bổ sung
            </h3>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
                <div className="space-y-1">
                    <FieldLabel text="Chiều cao" />
                    <input
                        value={values.height || ''}
                        onChange={(event) => onChange('height', event.target.value)}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none"
                        placeholder="VD: 20-40cm"
                    />
                </div>
                <div className="space-y-1">
                    <FieldLabel text="Chiều cao lóng" />
                    <input
                        value={values.internode_height || ''}
                        onChange={(event) => onChange('internode_height', event.target.value)}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none"
                        placeholder="VD: 10-15cm"
                    />
                </div>
                <div className="space-y-1">
                    <FieldLabel text="Đường kính tán" />
                    <input
                        value={values.canopy_diameter || ''}
                        onChange={(event) => onChange('canopy_diameter', event.target.value)}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none"
                        placeholder="VD: 15-25cm"
                    />
                </div>
                <div className="space-y-1">
                    <FieldLabel text="Đường kính thân" />
                    <input
                        value={values.trunk_diameter || ''}
                        onChange={(event) => onChange('trunk_diameter', event.target.value)}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none"
                        placeholder="VD: 5-10cm"
                    />
                </div>
                <div className="space-y-1">
                    <FieldLabel text="Đường kính gốc" />
                    <input
                        value={values.base_diameter || ''}
                        onChange={(event) => onChange('base_diameter', event.target.value)}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none"
                        placeholder="VD: 10-15cm"
                    />
                </div>
                <div className="space-y-1">
                    <FieldLabel text="Quy cách bầu" />
                    <input
                        value={values.pot_spec || ''}
                        onChange={(event) => onChange('pot_spec', event.target.value)}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none"
                        placeholder="VD: Chậu nhựa treo"
                    />
                </div>
                <div className="space-y-1">
                    <FieldLabel text="Giá thể" />
                    <input
                        value={values.substrate || ''}
                        onChange={(event) => onChange('substrate', event.target.value)}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none"
                        placeholder="VD: Đất hữu cơ"
                    />
                </div>
                <div className="space-y-1">
                    <FieldLabel text="Nhu cầu nước" />
                    <select
                        value={values.water_requirement || ''}
                        onChange={(event) => onChange('water_requirement', event.target.value)}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none"
                    >
                        <option value="">Chọn nhu cầu</option>
                        <option value="Thấp">Thấp</option>
                        <option value="Trung bình">Trung bình</option>
                        <option value="Cao">Cao</option>
                    </select>
                </div>
                <div className="space-y-1">
                    <FieldLabel text="Ánh sáng" />
                    <input
                        value={values.light_requirement || ''}
                        onChange={(event) => onChange('light_requirement', event.target.value)}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none"
                        placeholder="VD: Nắng nhẹ"
                    />
                </div>
                <div className="space-y-1">
                    <FieldLabel text="Mùa vụ" />
                    <input
                        value={values.seasonality || ''}
                        onChange={(event) => onChange('seasonality', event.target.value)}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none"
                        placeholder="VD: Quanh năm"
                    />
                </div>
                <div className="space-y-1">
                    <FieldLabel text="Hạn sử dụng" />
                    <input
                        value={values.expiry_date || ''}
                        onChange={(event) => onChange('expiry_date', event.target.value)}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none"
                        placeholder="VD: 12 tháng"
                    />
                </div>
            </div>
        </div>
    );
}
